<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <!-- General Section Settings -->
        <div class="mb-4">
          <label class="label-input">Section Settings</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Section Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor Link" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild
            :use-background-mobile="true"
            :settings="fields.settings"
            :container-page="fields.containerPage"
          />
        </div>

        <!-- Layout & Design Settings -->
        <div class="mb-4">
          <label class="label-input">Layout Settings</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="fields.gapCard" type="number" label="Card Gap" suffix="px" hide-details />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="fields.orderMedia"
                  type="number"
                  label="Media Order (e.g. 1)"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="fields.orderContent"
                  type="number"
                  label="Content Order (e.g. 2)"
                  hide-details
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- Media Config (Video or Image) -->
        <div class="mb-4">
          <label class="label-input">Media Configuration</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <v-select v-model="fields.useMedia" :items="mediaTypeOptions" label="Use Media Type" hide-details />
              </v-col>
            </v-row>

            <!-- Video fields -->
            <v-row v-if="fields.useMedia === 'video'">
              <v-col cols="12">
                <v-text-field
                  v-model="fields.videoCard"
                  label="Video Source URL"
                  placeholder="https://..."
                  hide-details
                />
              </v-col>
            </v-row>

            <!-- Image fields -->
            <v-row v-else-if="fields.useMedia === 'image'">
              <v-col cols="12">
                <UploadImage
                  @callback="handleUploadImageCard"
                  :image="{ imageSrc: fields.imageCard || '' }"
                  :height="`200`"
                  :typeUpload="'image'"
                  @callbackDelete="handleRemoveImageCard"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- Main Content (Title & Description) -->
        <div class="mb-4">
          <label class="label-input">Main Content Block</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.titleDesContent" :config="editorConfig" />
          </div>
        </div>

        <!-- Swiper Slides -->
        <div class="mb-4">
          <div class="d-flex justify-space-between align-center">
            <label class="label-input mb-0">Slides</label>
            <v-btn small text color="primary" @click="handleAddSlide">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Slide</span>
            </v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.slides" :key="`losaprily-slide-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>Slide {{ index + 1 }}: {{ getSlideTitle(item) }}</span>
                    <v-btn icon @click.stop="handleRemoveSlide(index)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="mb-3">
                    <label class="d-block mb-1">Slide Image</label>
                    <UploadImage
                      @callback="obj => handleUploadSlideImage(obj, index)"
                      :image="{ imageSrc: item.image || '' }"
                      :height="`160`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveSlideImage(index)"
                    />
                  </div>
                  <div class="mt-4">
                    <label class="mb-2 d-block">Slide Content</label>
                    <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

export default {
  name: 'card-media-with-slider-losaprily-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      mediaTypeOptions: [
        { text: 'Video', value: 'video' },
        { text: 'Image', value: 'image' },
      ],
      fields: {
        title: 'Card Media With Slider Losaprily Section',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        gapCard: 30,
        useMedia: 'video',
        orderMedia: 1,
        orderContent: 2,
        videoCard: '',
        imageCard: '',
        titleDesContent: '',
        slides: [],
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        slides: Array.isArray(extract.slides) ? extract.slides : this.fields.slides,
      };
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCardMediaWithSliderLosaprily(
        this.sectionEditor.section.html,
        this.fields,
      );
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleUploadImageCard(obj) {
      this.fields.imageCard = obj.imageSrc || '';
    },
    handleRemoveImageCard() {
      this.fields.imageCard = '';
    },
    handleAddSlide() {
      this.fields.slides.push({
        image: '',
        content: '<h3>New Slide Title</h3><p>New slide description content.</p>',
      });
    },
    handleRemoveSlide(index) {
      this.fields.slides.splice(index, 1);
    },
    handleUploadSlideImage(obj, index) {
      if (!this.fields.slides[index]) return;
      this.$set(this.fields.slides[index], 'image', obj.imageSrc || '');
    },
    handleRemoveSlideImage(index) {
      if (!this.fields.slides[index]) return;
      this.$set(this.fields.slides[index], 'image', '');
    },
    getSlideTitle(item) {
      if (!item || !item.content) return 'Untitled';
      const parser = new DOMParser();
      const doc = parser.parseFromString(item.content, 'text/html');
      const h3 = doc.querySelector('h3');
      if (h3 && h3.textContent.trim()) {
        return h3.textContent.trim();
      }
      const text = doc.body.textContent.trim();
      return text.substring(0, 30) || 'Untitled';
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
